// Segment labels for a deployment whose audience is not the client.
//
// The traveler archetypes this product segments on — Drifters, Elites,
// Culturalists, Growth-seekers, Flair-seekers, Achievers, Bond-seekers,
// Belongers — are the CLIENT'S segmentation research. They are the output of
// their own study, they are named in their own decks, and the demo published
// all seven of them verbatim: the same names, in the same order, carrying the
// same shares of spend. That is the single clearest way for a reader of the
// demo to recognise whose work it is, and the client raised it on 2026-08-20
// alongside the camels/falconry activity label.
//
// This module is the one place the demo's replacements are written down, and
// BOTH halves of the product read it, because both publish these names:
//
//   · Product Matching 1.3 — `SEGMENT_PREFERENCES` (server, seven segments,
//     hyphenated ids).
//   · Segmentation 1.2/1.3 — the pipeline's assigned `profiles.segment`
//     (snake_case ids), rendered by `segmentation-ui/engine.ts`.
//
// The two taxonomies overlap but are not identical: 1.3 has `belongers` and
// `anchored-achievers` where the pipeline has `bond_seekers` and `achievers`.
// Both spellings are keyed here, normalised, so neither section can be relabelled
// without the other and neither can drift onto a name the other does not use.
//
// ONLY THE DISPLAY NAME CHANGES. Ids, shares of spend, preference percentages,
// demand weights and every member assignment are untouched — this is applied at
// the payload boundary on the server and at render time in the web app, never
// where a value is computed or joined. The client's own deployment is served its
// own names, which is why every caller passes the tenant rather than a flag.

import { DEFAULT_TENANT, type TenantId } from "./tenant.js";

/** `Growth-Seekers`, `growth_seekers` and `Growth seekers` are one key. */
const normalize = (key: string): string =>
  key
    .toLowerCase()
    .replace(/[\s_-]+/g, "_")
    .replace(/[^a-z_]/g, "");

/**
 * Client archetype → the name the demo publishes.
 *
 * Behaviour-led rather than evocative, and deliberately matching no published
 * segmentation framework: the demo has to read like a real segmentation without
 * being anyone's. Keyed by BOTH taxonomies' ids (see the header) — the two rows
 * that differ, `bond_seekers`/`belongers` and `achievers`/`anchored_achievers`,
 * never appear in the same list, so `bond_seekers` and `belongers` sharing a
 * replacement cannot produce a duplicate on screen.
 */
const REPLACEMENTS: Record<string, string> = {
  drifters: "Free Roamers",
  elites: "Premium Seekers",
  growth_seekers: "Discovery Learners",
  flair_seekers: "Trendsetters",
  culturalists: "Heritage Immersers",
  // 1.3's seventh segment and the pipeline's equivalent.
  belongers: "Connection Seekers",
  bond_seekers: "Connection Seekers",
  // 1.3 qualifies this one; the pipeline does not, and the client's own decks
  // treat them as different reads, so they get different replacements.
  anchored_achievers: "Comfort Achievers",
  achievers: "Milestone Travellers",
};

/**
 * The demo-facing name for one segment, or the client's own where the tenant IS
 * the client.
 *
 * `fallback` is what the caller would otherwise have rendered — the stored name
 * on the server, the humanised id in the web app. An id with no replacement
 * (`unclassified`, and `faith_traveller`, which the demo's seed makes
 * unreachable) falls through unchanged rather than throwing: a segment the demo
 * never assigns must not be able to break the page if it ever does.
 */
export function demoSegmentLabel(tenant: TenantId, id: string, fallback: string): string {
  if (tenant === DEFAULT_TENANT) return fallback;
  return REPLACEMENTS[normalize(id)] ?? REPLACEMENTS[normalize(fallback)] ?? fallback;
}

/** Every client archetype this module knows how to relabel. Exported for the
 *  tests that assert the two taxonomies are fully covered. */
export const DEMO_SEGMENT_KEYS: readonly string[] = Object.keys(REPLACEMENTS);
